import { Book } from "core/Data/Book";
import { BookBundleService } from "shared/Services/BookBundleService";
import { isMyonApiSuccess, MyonApiResponse } from "shared/Api/MyonApiResponse";
import { HttpClient } from "silver";

export interface BookSchema {
	archiveCode: string;
	arPoints: null;
	arQuiz: null;
	arQuizLevel: number|null;
	audioRuntimeFriendly: string;
	audioRuntimeSecs: number|null;
	author: string|null;
	avgReview: number;
	bookBrandId: number;
	bookBrandTitle: string;
	bookId: number;
	bookPublisherId: number;
	bookPublisherLogo: string;
	bookPublisherTitle: string;
	bookSeriesId: number;
	bookSeriesTitle: string;
	copyright: number;
	coverUrl: string;
	coverUrlCropped: string;
	dateAdded: string|null;
	deleted: boolean;
	descr: string;
	fiction: boolean;
	graphic: boolean;
	guidedReadingLevel: string|null;
	hasBookQuiz: boolean;
	illustrator: string|null;
	interestLevel: string|null;
	isbnCode: string;
	languages: string;
	languageIds: number[];
	lexileLabel: string|null;
	lexileLevel: number|null;
	manifestXml: string;
	minUserGradeLevelId: number|null;
	maxUserGradeLevelId: number|null;
	minGradeBookLastModified: string;
	minimumGradeBookAccess: null|string;
	accountMinGradeBookLastModified?: string;
	accountMinimumGradeBookAccess?: null|number;
	myRating: number;
	onMyList: boolean;
	pagecount: number|null;
	readingLevel: string|null;
	reviewsLink: string;
	scholasticReadingCount: number|null;
	cefrLevelId: number|null;
	title: string;
	titleShort: string;
	version: number;
	wordcount: number|null;
}

class BookFactory {
	public makeFromSchema(data: BookSchema): Book {
		return new Book(
			data.archiveCode,
			data.bookPublisherId,
			data.bookPublisherTitle,
			data.bookPublisherLogo,
			data.bookId,
			data.bookBrandId,
			data.bookBrandTitle,
			data.bookSeriesId,
			data.bookSeriesTitle,
			data.title,
			data.titleShort,
			data.descr,
			data.author,
			data.illustrator,
			data.pagecount,
			data.wordcount,
			data.audioRuntimeSecs,
			data.audioRuntimeFriendly,
			data.readingLevel,
			data.guidedReadingLevel,
			data.arQuiz,
			data.arPoints,
			data.arQuizLevel,
			data.hasBookQuiz,
			data.scholasticReadingCount,
			data.cefrLevelId,
			data.interestLevel,
			data.lexileLevel,
			data.lexileLabel,
			data.minUserGradeLevelId,
			data.maxUserGradeLevelId,
			data.copyright,
			data.fiction,
			data.graphic,
			data.version,
			data.deleted,
			data.dateAdded,
			data.coverUrl,
			data.coverUrlCropped,
			data.minimumGradeBookAccess ? parseInt(data.minimumGradeBookAccess, 10) : null,
			data.minGradeBookLastModified,
			data.manifestXml,
			data.languages,
			data.languageIds,
			data.avgReview,
			data.myRating,
			data.reviewsLink,
			data.onMyList,
			data.accountMinimumGradeBookAccess,
			data.accountMinGradeBookLastModified
		);
	}
}

export const NoBookData = null;
export class BookDataService{

	public static readonly NoBookData: typeof NoBookData = NoBookData;

	private bookDataCache: { [ bookId: string ]: Promise<Book|typeof NoBookData>} = { };
	private bookFactory = new BookFactory();

	constructor(
		private httpClient: HttpClient,
		private bookBundleService: BookBundleService
	) {
		this.bookBundleService.onBeforeFavoriteUpdate(({ bookId, favorited }) => {
			if (this.bookDataCache.hasOwnProperty(bookId)) {
				this.getFromId(bookId).then(book => book.onMyList = favorited);
			}
		});
	}

	private createBookDataFromObject(data: BookSchema): Book {
		return this.bookFactory.makeFromSchema(data);
	}

	/**
	 * Manually inputs book data into this objects cache. To be used when data
	 * is known on page load.
	 */
	public injectBookData(bookDataMap: { [ bookId: string ]: BookSchema }): { [ bookId: string ]: Book } {
		const out: { [ bookId: string ]: Book } = { };
		for (const bookId in bookDataMap) {
			if (! bookDataMap.hasOwnProperty(bookId)) {
				continue;
			}
			const book = this.createBookDataFromObject(bookDataMap[ bookId ]);
			this.bookDataCache[ bookId ] = Promise.resolve(book);
			out[bookId] = book;
		}
		return out;
	}

	private async loadBooksAjax(bookIds: number[]): Promise<void> {
		const request: Promise<MyonApiResponse<{ [bookId: string]: BookSchema}>> = this.httpClient.post("/api/books.json", {
			body: { bookId: bookIds }
		});

		for (let i = 0; i < bookIds.length; i++) {
			const id = bookIds[ i ];
			this.bookDataCache[id] = (async () => {
				const response = await request;
				if (!isMyonApiSuccess(response)) {
					delete this.bookDataCache[ id ];
				}
				if (!response.result.hasOwnProperty(id)) {
					return NoBookData;
				}
				return this.createBookDataFromObject(response.result[id]);
			})();
		}


		await request;
	}

	public async getFromIds(bookIds: number[]): Promise<{ [bookId: string]: Book|typeof NoBookData}> {
		const out: { [bookId: string]: Book|typeof NoBookData } = { };
		const promises: Array<Promise<any>> = [];

		const uncached: number[] = bookIds.filter((bookId: number): boolean => {
			return !this.bookDataCache.hasOwnProperty(bookId);
		});

		if (uncached.length > 0) {
			const ajaxPromises: Array<Promise<void>> = [];
			const ajaxPageLength: number = 500;

			for (let i = 0; i < uncached.length; i += ajaxPageLength) {
				ajaxPromises.push(this.loadBooksAjax(uncached.slice(i, i + ajaxPageLength)));
			}
			await Promise.all(ajaxPromises);
		}

		for (let i = 0; i < bookIds.length; i++) {
			const id = bookIds[ i ];
			promises.push(this.bookDataCache[ id ].then((value) => {
				out[id] = value;
			}));
		}

		await Promise.all(promises);
		return out;
	}

	public async getFromId(bookId: number): Promise<Book> {
		const byId = await this.getFromIds([ bookId ]);
		if (!byId.hasOwnProperty(bookId) || byId[ bookId ] === NoBookData) {
			throw new Error("Could not find book where id is `" + bookId + "`");
		}
		return byId[bookId] as Book;
	}

	public getCachedFromId(bookId: number): Promise<Book|null>|undefined {
		return this.bookDataCache[bookId] ?? undefined;
	}
}
